import { ReasonInput } from '@strata/react-compound-inputs';
import { DateInputFormik } from '@strata/react-date-time';
import { DialogWithForm } from '@strata/react-dialog';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import dayjs from 'dayjs';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { type IntakeCase, usePostPauseIntakeCaseMutation } from 'store/api/intakeCaseApi';

const ValidationSchema = Yup.object({
    reminderDate: DateInputFormik.validator().required(
        validationStrings.requiredField.errorMessage,
    ),
    ...ReasonInput.validationSchema,
});

interface PauseIntakeCaseDialogProps {
    isOpen: boolean;
    onClose: () => void;
    intakeCase: Pick<IntakeCase, 'intakeCaseId'>;
}

export const PauseIntakeCaseDialog = ({
    isOpen,
    onClose,
    intakeCase,
}: PauseIntakeCaseDialogProps) => {
    const [postPauseIntakeCase] = usePostPauseIntakeCaseMutation();

    return (
        <Formik
            initialValues={ReasonInput.initialValues as Yup.InferType<typeof ValidationSchema>}
            validationSchema={ValidationSchema}
            enableReinitialize
            onSubmit={async ({ reminderDate, ...rest }, { resetForm }) => {
                const { reason } = ReasonInput.submitValues(rest);
                await postPauseIntakeCase({
                    intakeCaseId: intakeCase.intakeCaseId,
                    reminderDate,
                    reason,
                }).unwrap();
                SuccessToast({ message: 'Intake case snoozed successfully.' });
                resetForm();
                onClose();
            }}
        >
            {(formik) => (
                <DialogWithForm
                    isOpen={isOpen}
                    dialogTitle="Snooze Intake Case"
                    isPrimaryButtonDisabled={
                        !(formik.dirty && formik.isValid) || formik.isSubmitting
                    }
                    onSecondaryButtonClick={() => {
                        onClose();
                        formik.resetForm();
                    }}
                    primaryButtonLabel="Save"
                    secondaryButtonLabel="Cancel"
                    dialogContent={
                        <>
                            <ReasonInput
                                hideSource
                                reasons={[
                                    'PA initiated by other means',
                                    'FMH monitoring for PA decision',
                                    'Pending provider response',
                                    'Incomplete/missing information',
                                ]}
                            />
                            <DateInputFormik
                                id="reminderDate"
                                label="Reminder to resume"
                                required
                                minDate={dayjs().add(1, 'day').startOf('day').toDate()}
                                maxDate={dayjs().add(1, 'month').endOf('day').toDate()}
                            />
                        </>
                    }
                />
            )}
        </Formik>
    );
};
